import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/utils/formatINR";
import RoundCompareView from "@/components/RoundCompareView";
import SearchableSelect from "@/components/SearchableSelect";

export default function NpdCompare() {
  const [params, setParams] = useSearchParams();
  const [npds, setNpds] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leftCustomerFilter, setLeftCustomerFilter] = useState("");    // "" = all customers (left picker)
  const [rightCustomerFilter, setRightCustomerFilter] = useState("");  // "" = all customers (right picker)

  // Left side state
  const [leftNpd, setLeftNpd] = useState(null);
  const [leftRounds, setLeftRounds] = useState([]);
  const [leftRoundId, setLeftRoundId] = useState("latest"); // "latest" or round_id

  // Right side state
  const [rightNpd, setRightNpd] = useState(null);
  const [rightRounds, setRightRounds] = useState([]);
  const [rightRoundId, setRightRoundId] = useState("latest");

  const [loadingSide, setLoadingSide] = useState(null);

  const leftId = params.get("a") || "";
  const rightId = params.get("b") || "";

  // Initial: fetch full list of NPDs (admin sees everything, others scope-filtered server-side) + customers.
  useEffect(() => {
    api.get("/npd", { params: { limit: 500, exclude_assembly_children: false } })
      .then((r) => setNpds(r.data.npds || []))
      .catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);
  const customerName = (id) => customers.find((c) => c.customer_id === id)?.name || "";

  const loadSide = async (id, setNpd, setRounds, setRoundSel, sideTag) => {
    if (!id) { setNpd(null); setRounds([]); setRoundSel("latest"); return; }
    setLoadingSide(sideTag);
    try {
      const [npdRes, roundsRes] = await Promise.all([
        api.get(`/npd/${id}`),
        api.get(`/npd/${id}/rounds`).catch(() => ({ data: { rounds: [] } })),
      ]);
      setNpd(npdRes.data);
      setRounds(roundsRes.data?.rounds || []);
      setRoundSel("latest");
    } finally { setLoadingSide(null); }
  };

  useEffect(() => { loadSide(leftId, setLeftNpd, setLeftRounds, setLeftRoundId, "left"); /* eslint-disable-next-line */ }, [leftId]);
  useEffect(() => { loadSide(rightId, setRightNpd, setRightRounds, setRightRoundId, "right"); /* eslint-disable-next-line */ }, [rightId]);

  const sortedNpds = useMemo(
    () => [...npds].sort((a, b) => (b.npd_number || "").localeCompare(a.npd_number || "")),
    [npds]
  );
  // Customer dropdown options — only customers that actually have costings here.
  const customerOptions = useMemo(() => {
    const ids = new Set(npds.map((n) => n.customer_id).filter(Boolean));
    const opts = customers
      .filter((c) => ids.has(c.customer_id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((c) => ({ value: c.customer_id, label: c.name }));
    return [{ value: "", label: "All customers" }, ...opts];
  }, [customers, npds]);
  // Build the picker options, optionally filtered to one customer. Each side gets its own.
  const buildOptions = (custId) => sortedNpds
    .filter((n) => !custId || n.customer_id === custId)
    .map((n) => {
      const cn = customerName(n.customer_id);
      return {
        value: n.npd_id,
        label: `${n.npd_number} — ${n.part_name || "(unnamed)"}${n.part_number ? ` · ${n.part_number}` : ""}${cn ? ` · ${cn}` : ""}`,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const leftOptions = useMemo(() => buildOptions(leftCustomerFilter), [sortedNpds, leftCustomerFilter, customers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rightOptions = useMemo(() => buildOptions(rightCustomerFilter), [sortedNpds, rightCustomerFilter, customers]);

  const setLeft = (id) => { const n = new URLSearchParams(); if (id) n.set("a", id); if (rightId) n.set("b", rightId); setParams(n); };
  const setRight = (id) => { const n = new URLSearchParams(); if (leftId) n.set("a", leftId); if (id) n.set("b", id); setParams(n); };
  const swap = () => { const n = new URLSearchParams(); if (rightId) n.set("a", rightId); if (leftId) n.set("b", leftId); setParams(n); };

  // Resolve which snapshot to feed RoundCompareView for each side.
  // "latest" → use the live NPD doc.
  // Specific round_id → use that round's snapshot.
  const resolveSide = (npd, rounds, roundSel) => {
    if (!npd) return null;
    if (roundSel === "latest") {
      return {
        round_id: `current-${npd.npd_id}`,
        round_label: `${npd.npd_number} (Current)`,
        round_date: npd.updated_at || npd.created_at || new Date().toISOString(),
        snapshot: npd,
      };
    }
    const r = rounds.find((x) => x.round_id === roundSel);
    if (!r) return null;
    return {
      round_id: r.round_id,
      round_label: `${npd.npd_number} · ${r.round_label || r.round_id}`,
      round_date: r.round_date || r.created_at,
      snapshot: r.snapshot || {},
    };
  };

  const leftSide = useMemo(() => resolveSide(leftNpd, leftRounds, leftRoundId), [leftNpd, leftRounds, leftRoundId]);
  const rightSide = useMemo(() => resolveSide(rightNpd, rightRounds, rightRoundId), [rightNpd, rightRounds, rightRoundId]);

  const sameSelected = leftId && rightId && leftId === rightId && leftRoundId === rightRoundId;

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto" data-testid="npd-compare-page">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/npd" className="p-2 hover:bg-slate-100 rounded-md text-slate-500"><ArrowLeft size={18} /></Link>
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><FileText size={20} className="text-orange-600" /> Compare two costings</h1>
          <p className="text-xs text-slate-500 mt-0.5">Pick any two NPD costings. Defaults to the latest round of each — override per side.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* LEFT */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Left costing</div>
          {/* Per-side customer filter */}
          <div className="mb-2">
            <SearchableSelect
              value={leftCustomerFilter}
              onChange={setLeftCustomerFilter}
              options={customerOptions}
              placeholder="Filter by customer (optional)"
              testid="npd-compare-left-customer"
              className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-slate-50"
            />
          </div>
          <SearchableSelect
            value={leftId}
            onChange={setLeft}
            options={leftOptions}
            placeholder="Search & pick left costing…"
            testid="npd-compare-left-picker"
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
          />
          {leftNpd && (
            <div className="mt-2">
              <select value={leftRoundId} onChange={(e) => setLeftRoundId(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white" data-testid="npd-compare-left-round">
                <option value="latest">Latest (current)</option>
                {leftRounds.map((r) => (
                  <option key={r.round_id} value={r.round_id}>{r.round_label || r.round_id}</option>
                ))}
              </select>
              <div className="mt-3 text-xs text-slate-700">
                <div className="font-mono text-slate-500">{leftNpd.npd_number}</div>
                <div className="font-medium">{leftNpd.part_name}</div>
                {leftNpd.grand_total != null && (
                  <div className="mt-1 font-mono text-base text-slate-900">{formatINR(leftNpd.grand_total)}</div>
                )}
              </div>
            </div>
          )}
          {loadingSide === "left" && <div className="mt-2 text-xs text-slate-400 italic">Loading…</div>}
        </div>

        {/* RIGHT */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Right costing</div>
            {leftId && rightId && !sameSelected && (
              <button onClick={swap} className="text-[10px] text-sky-700 hover:text-sky-900 underline" data-testid="npd-compare-swap">Swap ↔</button>
            )}
          </div>
          {/* Per-side customer filter */}
          <div className="mb-2">
            <SearchableSelect
              value={rightCustomerFilter}
              onChange={setRightCustomerFilter}
              options={customerOptions}
              placeholder="Filter by customer (optional)"
              testid="npd-compare-right-customer"
              className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-slate-50"
            />
          </div>
          <SearchableSelect
            value={rightId}
            onChange={setRight}
            options={rightOptions}
            placeholder="Search & pick right costing…"
            testid="npd-compare-right-picker"
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
          />
          {rightNpd && (
            <div className="mt-2">
              <select value={rightRoundId} onChange={(e) => setRightRoundId(e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white" data-testid="npd-compare-right-round">
                <option value="latest">Latest (current)</option>
                {rightRounds.map((r) => (
                  <option key={r.round_id} value={r.round_id}>{r.round_label || r.round_id}</option>
                ))}
              </select>
              <div className="mt-3 text-xs text-slate-700">
                <div className="font-mono text-slate-500">{rightNpd.npd_number}</div>
                <div className="font-medium">{rightNpd.part_name}</div>
                {rightNpd.grand_total != null && (
                  <div className="mt-1 font-mono text-base text-slate-900">{formatINR(rightNpd.grand_total)}</div>
                )}
              </div>
            </div>
          )}
          {loadingSide === "right" && <div className="mt-2 text-xs text-slate-400 italic">Loading…</div>}
        </div>
      </div>

      {sameSelected && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-xs text-amber-800 mb-4">
          Pick two different costings (or different rounds of the same costing) to compare.
        </div>
      )}

      {leftSide && rightSide && !sameSelected && (
        <RoundCompareView
          round={leftSide}
          currentNpd={rightSide.snapshot}
          rightLabel={rightSide.round_label}
        />
      )}

      {!leftNpd && !rightNpd && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500">
          Pick two costings above to see a side-by-side comparison.
        </div>
      )}
    </div>
  );
}
